import React from 'react';
import { useTranslation } from 'react-i18next';

import { Sheet, useSheetDismiss } from '../../../../components/Sheet';
import { Button } from '../../../../components/Button';
import { Field } from '../../../../components/Field';
import { TextArea } from '../../../../components/TextArea';
import type { BlogHero } from '../../../../mock-data/types';
import { Body, Preview, Hint } from './HeroDetailsSheet.styles';

export type HeroDetailsSheetProps = {
  hero: BlogHero;
  /** Committed on Save; a cancel leaves the draft's hero untouched. */
  onSave: (hero: BlogHero) => void;
  onClose: () => void;
};

/**
 * Alt text and caption for the hero image, edited together in one dialog.
 *
 * They were two inputs stacked beside the thumbnail, which spent the widest row
 * of the form on copy that is written once and rarely revisited. They belong
 * together anyway: both describe the same image, and seeing it while writing
 * about it is the whole job, so the dialog leads with the picture.
 *
 * Edits are local until Save, so an author who opens the dialog to check the alt
 * text and closes it again changes nothing.
 */
export const HeroDetailsSheet = ({ hero, onSave, onClose }: HeroDetailsSheetProps): React.ReactElement => {
  const { t } = useTranslation();
  const { closeHandle, dismiss } = useSheetDismiss(onClose);
  const [alt, setAlt] = React.useState(hero.alt);
  const [caption, setCaption] = React.useState(hero.caption);

  return (
    <Sheet
      title={t('Hero image details')}
      onClose={onClose}
      closeHandle={closeHandle}
      footer={
        <>
          <Button label={t('Cancel')} variant="outline" onClick={dismiss} />
          <Button
            label={t('Save')}
            onClick={() => {
              onSave({ ...hero, alt, caption });
              dismiss();
            }}
          />
        </>
      }
    >
      <Body>
        <Preview src={hero.url} alt={alt || t('Hero image preview')} />
        <TextArea
          label={t('Alt text')}
          rows={2}
          value={alt}
          onChange={setAlt}
          placeholder={t('Describe the image for readers who cannot see it')}
        />
        <Hint text="Alt text also becomes the image description search engines read." tag="span" />
        <Field
          label={t('Caption')}
          value={caption}
          onChange={setCaption}
          placeholder={t('Credit or context, shown under the cover')}
        />
      </Body>
    </Sheet>
  );
};

export default HeroDetailsSheet;
